import { Calendar, Modal, Tooltip, Button } from 'antd';
import { format } from 'date-fns';
import {
  IconClock,
  IconUsers,
  IconBook,
  IconUser,
  IconVideo,
  IconBuilding,
  IconCalendarEvent
} from '@tabler/icons-react';

import { Box, Screen } from '../../components';
import { useBranch } from '../../hooks/useBranch';
import { getPercentageCorrect, useGetDashboardData } from '../../services/userQuestion';

import './DashboardPage.scss';
import { OppositionBasesBanner, SubjectResult } from './components';
import { FailedQuestions } from './components/FailedQuestions';
import { TestResults } from './components/TestResults';
import { useGetSessionsDashboard } from '../../services/session';
import { useState } from 'react';
import { type TSession, Kind } from '../../types/session';
import { Loader } from '../../components/Loader/Loader';
import { useTheme } from '../../contexts/ThemeContext';
import clsx from 'clsx';
import { es } from 'date-fns/locale';

// Function to format kind for display
const formatKind = (kind: Kind): string => {
  switch (kind) {
    case Kind.Online:
      return 'ONLINE';
    case Kind.Presencial:
      return 'PRESENCIAL';
    default:
      return 'OTRO';
  }
};

// Function to get appropriate icon based on session kind
const getKindIcon = (kind: Kind) => {
  switch (kind) {
    case Kind.Online:
      return <IconVideo className="kind-icon" />;
    case Kind.Presencial:
      return <IconBuilding className="kind-icon" />;
    default:
      return null;
  }
};

interface Props {
  marked: boolean
}

const CalendarMarker = ({ marked }: Props) => {
  if (!marked) return null;

  return (
    <div className={clsx(
      'absolute inset-0 rounded-full pointer-events-none',
      'bg-gradient-to-br from-blue-500/20 to-purple-500/20',
      'dark:from-blue-400/30 dark:to-purple-400/30',
      'border-2 border-blue-500 dark:border-blue-400',
      'animate-pulse'
    )} />
  );
};

const DashboardPage = () => {
  const { branch } = useBranch();
  const { theme } = useTheme();

  const { sessions, loading } = useGetSessionsDashboard();

  const { dashboardData } = useGetDashboardData();

  const [modalEvents, setModalEvents] = useState(false);

  const [selectedEvents, setSelectedEvents] = useState<TSession[]>();

  const worst = dashboardData[0];
  const best = dashboardData[dashboardData.length - 1];

  const sessionsStrings = (sessions || [])?.map((s) => format(new Date(s.date), 'dd/MM/yyyy'));

  const handleSelectCalendar = (date: Date) => {
    const formatted = format(date, 'dd/MM/yyyy');

    const events = sessions?.filter((session) => format(new Date(session.date), 'dd/MM/yyyy') === formatted);

    if ((events || []).length > 0) {
      setSelectedEvents(events);
      setModalEvents(true);
    }
  };

  console.log('event', selectedEvents);


  return (
    <Screen id="dashboard" title={branch?.name ?? 'Dashboard'}>
      <OppositionBasesBanner />
      <div className='section-container'>
        <Box flex={3} title="Resultados en tests" defaultCollpsed className="order-3 md:order-1">
          <TestResults />
        </Box>
        <Box flex={1} title="Mejor materia" defaultCollpsed={false} className="order-1 md:order-2">
          {best && (
            <SubjectResult type="success" name={best?.[0]} score={getPercentageCorrect(best?.[1])} />
          )}
        </Box>
        <Box flex={1} title="Peor materia" defaultCollpsed={false} className="order-2 md:order-3">
          {worst && (
            <SubjectResult type="danger" name={worst?.[0]} score={getPercentageCorrect(worst?.[1])} />
          )}
        </Box>
      </div>
      <div className='section-container'>
        <div className='calendar'>
          {loading
            ? (
              <Loader size={100} />
            )
            : (
              <>
                <Box flex={2} title="Calendario" defaultCollpsed className="h-full flex flex-col">
                  <div className={clsx(
                    'calendar-wrapper flex-1',
                    theme === 'dark' ? 'dark-calendar' : 'light-calendar'
                  )}>
                    <Calendar
                      rootClassName={clsx(
                        'modern-calendar h-full',
                        theme === 'dark' ? 'dark-calendar' : 'light-calendar'
                      )}
                      fullscreen={false}
                      onSelect={(e: any) => handleSelectCalendar(e.$d)}
                      className={clsx(
                        'calendar-comp h-full',
                        theme === 'dark' ? 'dark-calendar' : 'light-calendar'
                      )}
                      cellRender={(date: any) => (
                        <div className="relative w-full h-full">
                          <CalendarMarker marked={sessionsStrings.includes(format(new Date(date.$d), 'dd/MM/yyyy'))} />
                        </div>
                      )}
                    />
                  </div>
                </Box>
              </>
            )}
        </div>
        <Box flex={3} title="Preguntas a reforzar" defaultCollpsed>
          <FailedQuestions />
        </Box>
        {selectedEvents && (
          <Modal
            open={modalEvents}
            onCancel={() => setSelectedEvents(undefined)}
            footer={null}
            width={600}
            className="session-modal"
            closeIcon={null}
            title={null}
          >
            <div className={clsx(
              'session-modal-hero',
              selectedEvents[0].kind === Kind.Presencial && 'kind-presencial',
              selectedEvents[0].kind === Kind.Online ? 'kind-online' : (!selectedEvents[0].kind || (selectedEvents[0].kind !== Kind.Online && selectedEvents[0].kind !== Kind.Presencial)) && 'kind-otro'
            )}>
              <div className="session-modal-hero-content">
                <div className="session-modal-date-eyebrow">
                  <IconCalendarEvent stroke={2} />
                  <span>{format(new Date(selectedEvents[0].date), "EEEE d 'de' MMMM", { locale: es })}</span>
                </div>
                <h2 className="session-modal-title">
                  {selectedEvents.length === 1 ? 'Sesión programada' : `${selectedEvents.length} sesiones programadas`}
                </h2>
                <p className="session-modal-subtitle">
                  {selectedEvents.length === 1
                    ? 'Tienes una cita en tu calendario académico'
                    : 'Tienes varias citas en tu calendario académico'}
                </p>
              </div>
            </div>

            <div className="session-event-list">
              {selectedEvents.map((ev, index) => (
                <div
                  key={ev._id || index}
                  className={clsx(
                    'session-event-card',
                    ev.kind === Kind.Online && 'kind-online',
                    ev.kind === Kind.Presencial && 'kind-presencial',
                    ev.kind !== Kind.Online && ev.kind !== Kind.Presencial && 'kind-otro'
                  )}
                >
                  <div className="session-event-header">
                    <div className="session-event-title">
                      <h3 className="session-event-subject">
                        {typeof ev.subject === 'string' ? ev.subject : ev.subject?.name}
                      </h3>
                    </div>
                    <div className={clsx(
                      'session-event-kind-badge',
                      ev.kind === Kind.Online && 'kind-online',
                      ev.kind === Kind.Presencial && 'kind-presencial',
                      ev.kind !== Kind.Online && ev.kind !== Kind.Presencial && 'kind-otro'
                    )}>
                      {getKindIcon(ev.kind)}
                      <span>{formatKind(ev.kind)}</span>
                    </div>
                  </div>

                  <div className="session-event-time">
                    <IconClock stroke={2} />
                    <span>{format(ev.date, 'eee dd MMM - HH:mm', { locale: es })}</span>
                  </div>

                  <div className="session-event-details">
                    {typeof ev.teacher === 'string' ? ev.teacher : ev.teacher?.name ? (
                      <Tooltip title="Profesor">
                        <div className="session-event-detail-item">
                          <IconUser stroke={2} />
                          <span>{typeof ev.teacher === 'string' ? ev.teacher : ev.teacher?.name}</span>
                        </div>
                      </Tooltip>
                    ) : null}

                    {ev.spots && (
                      <Tooltip title="Cupo">
                        <div className="session-event-detail-item">
                          <IconUsers stroke={2} />
                          <span>{ev.spots} plazas</span>
                        </div>
                      </Tooltip>
                    )}

                    {(typeof ev.branch === 'string' ? ev.branch : ev.branch?.name) && (
                      <Tooltip title="Sede">
                        <div className="session-event-detail-item">
                          <IconBook stroke={2} />
                          <span>{typeof ev.branch === 'string' ? ev.branch : ev.branch?.name}</span>
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {ev.description && (
                    <div className="session-event-description">
                      {ev.description}
                    </div>
                  )}

                  {ev.meeting && (
                    <div className="session-event-actions">
                      <Button
                        type="primary"
                        icon={<IconVideo size={16} strokeWidth={2} />}
                        className="session-join-button"
                        href={ev.meeting.join_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Acceder a la reunión
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Modal>
        )}
      </div>
    </Screen>
  );
};

export { DashboardPage };
